import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Image, Input, Message, Popconfirm, Upload } from '@arco-design/web-react';
import { IconClose, IconDelete, IconImage, IconPlus, IconSend } from '@arco-design/web-react/icon';
import { baseUrl } from '../utils';
import {
  createSession,
  deleteSession,
  fetchMessage,
  fetchMessages,
  fetchSessions,
  retryMessage,
  sendMessage
} from './api';
import { ChatMessage, ChatSession } from './types';
import RegionEditor, { RegionCoords } from './RegionEditor';
import './index.scss';

const SUGGESTIONS = [
  '一只戴红帽子的橘猫，扁平插画风，白色背景',
  '赛博朋克风格的城市夜景，竖版构图',
  '把上传的图片改成水彩风格',
  '生成一张适合做公众号首图的海报，蓝白配色'
];

const POLL_INTERVAL = 2000;
const MEMORY_ROUNDS = 10;
const MAX_IMAGES = 4;

function formatTime(value?: number) {
  if (!value) return '';
  const date = new Date(value);
  const now = new Date();
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  if (date.toDateString() === now.toDateString()) {
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}

function toImageList(message: ChatMessage): string[] {
  const raw = message.inputImageUrls;
  if (Array.isArray(raw)) {
    return raw.filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean);
      }
    } catch {
      // 旧数据或非 JSON，回退到单图字段
    }
  }
  return message.inputImageUrl ? [message.inputImageUrl] : [];
}

export default function ImageChat() {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [regionImageUrl, setRegionImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const pollers = useRef<Record<number, number>>({});
  const activeIdRef = useRef<number | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);

  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  const scrollToBottom = useCallback((force: boolean) => {
    const body = bodyRef.current;
    if (!body || (!force && !stickRef.current)) return;
    body.scrollTop = body.scrollHeight;
  }, []);

  const handleBodyScroll = () => {
    const body = bodyRef.current;
    if (!body) return;
    stickRef.current = body.scrollHeight - body.scrollTop - body.clientHeight < 80;
  };

  const stopPoll = useCallback((id: number) => {
    const timer = pollers.current[id];
    if (timer) {
      window.clearInterval(timer);
      delete pollers.current[id];
    }
  }, []);

  const stopAllPolls = useCallback(() => {
    Object.keys(pollers.current).forEach(key => stopPoll(Number(key)));
  }, [stopPoll]);

  useEffect(() => () => stopAllPolls(), [stopAllPolls]);

  const startPoll = useCallback(
    (id: number) => {
      if (pollers.current[id]) return;
      pollers.current[id] = window.setInterval(async () => {
        try {
          const resp = await fetchMessage(id);
          const message = resp.data;
          setMessages(prev => prev.map(item => (item.id === id ? message : item)));
          if (message.status === 'SUCCESS' || message.status === 'FAILED') {
            stopPoll(id);
          }
        } catch {
          // 单次轮询失败不终止，等下一轮
        }
      }, POLL_INTERVAL);
    },
    [stopPoll]
  );

  const selectSession = useCallback(
    async (id: number) => {
      stopAllPolls();
      setActiveId(id);
      setMessages([]);
      setLoadingMessages(true);
      stickRef.current = true;
      try {
        const resp = await fetchMessages(id);
        const list = resp.data || [];
        setMessages(list);
        requestAnimationFrame(() => scrollToBottom(true));
        window.setTimeout(() => scrollToBottom(true), 400);
        list
          .filter(item => item.status === 'PENDING' || item.status === 'PROCESSING')
          .forEach(item => startPoll(item.id));
      } catch {
        setMessages([]);
      } finally {
        setLoadingMessages(false);
      }
    },
    [scrollToBottom, startPoll, stopAllPolls]
  );

  const loadSessions = useCallback(
    async (autoSelect: boolean) => {
      const resp = await fetchSessions();
      const list = resp.data || [];
      setSessions(list);
      if (autoSelect && !activeIdRef.current && list.length > 0) {
        void selectSession(list[0].id);
      }
    },
    [selectSession]
  );

  useEffect(() => {
    void loadSessions(true);
  }, [loadSessions]);

  const anyRunning = sessions.some(item => item.running);

  useEffect(() => {
    if (!anyRunning) return;
    const timer = window.setInterval(() => {
      void loadSessions(false).catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [anyRunning, loadSessions]);

  useEffect(() => {
    requestAnimationFrame(() => scrollToBottom(false));
    const timer = window.setTimeout(() => scrollToBottom(false), 400);
    return () => window.clearTimeout(timer);
  }, [messages, scrollToBottom]);

  const handleNewSession = async () => {
    try {
      const resp = await createSession();
      const id = resp.data.id;
      stopAllPolls();
      setActiveId(id);
      setMessages([]);
      setText('');
      setImageUrls([]);
      await loadSessions(false);
    } catch {
      // 请求层已经提示
    }
  };

  const handleDeleteSession = async (id: number) => {
    try {
      await deleteSession(id);
      const rest = sessions.filter(item => item.id !== id);
      setSessions(rest);
      if (activeIdRef.current === id) {
        stopAllPolls();
        setMessages([]);
        if (rest.length > 0) {
          void selectSession(rest[0].id);
        } else {
          setActiveId(null);
        }
      }
    } catch {
      // 请求层已经提示
    }
  };

  const uploadImage = async (file: File) => {
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      Message.error('只支持 PNG / JPG / WebP 图片');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      Message.error('图片不能超过 10MB');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch(`${baseUrl}/common/uploadFile`, {
        method: 'POST',
        headers: { Authorization: localStorage.getItem('authToken') || '' },
        body: form
      });
      const payload = (await response.json()) as { code?: number; data?: string; message?: string };
      if (payload.code === 200 && typeof payload.data === 'string') {
        const url = payload.data;
        setImageUrls(prev => {
          if (prev.length >= MAX_IMAGES) {
            Message.warning(`最多上传 ${MAX_IMAGES} 张图片`);
            return prev;
          }
          return [...prev, url];
        });
      } else {
        Message.error(payload.message || '图片上传失败');
      }
    } catch {
      Message.error('图片上传失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = (file: any) => {
    void uploadImage(file as File);
    return false;
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }
    if (files.length > 0) {
      event.preventDefault();
      void (async () => {
        for (const file of files) {
          await uploadImage(file);
        }
      })();
    }
  };

  const submitTurn = async (
    body: Record<string, any>,
    displayText: string,
    displayImages: string[]
  ): Promise<boolean> => {
    let sessionId = activeIdRef.current;
    setSending(true);
    try {
      if (!sessionId) {
        const created = await createSession();
        sessionId = created.data.id;
        setActiveId(sessionId);
      }
      const resp = await sendMessage(sessionId, body);
      const { userMessageId, assistantMessageId } = resp.data;
      const now = Date.now();
      const userMessage: ChatMessage = {
        id: userMessageId,
        sessionId,
        seq: 0,
        role: 'user',
        content: displayText || undefined,
        inputImageUrl: displayImages[0],
        inputImageUrls: displayImages,
        status: 'SUCCESS',
        createdAt: now
      };
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        sessionId,
        seq: 0,
        role: 'assistant',
        status: 'PENDING',
        createdAt: now
      };
      setMessages(prev => [...prev, userMessage, assistantMessage]);
      stickRef.current = true;
      requestAnimationFrame(() => scrollToBottom(true));
      startPoll(assistantMessageId);
      loadSessions(false).catch(() => undefined);
      return true;
    } catch {
      return false;
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    const content = text.trim();
    if (!content && imageUrls.length === 0) return;
    const body: Record<string, any> = { text: content || undefined };
    if (imageUrls.length > 0) {
      body.inputImageUrls = imageUrls;
    }
    const ok = await submitTurn(body, content, imageUrls);
    if (ok) {
      setText('');
      setImageUrls([]);
    }
  };

  const handleRegionSubmit = async (region: RegionCoords, instruction: string) => {
    if (!regionImageUrl) return;
    const content = `只修改框选区域：${instruction}`;
    const ok = await submitTurn(
      { text: content, inputImageUrls: [regionImageUrl], region },
      content,
      [regionImageUrl]
    );
    if (ok) {
      setRegionImageUrl(null);
    }
  };

  const handleRetry = async (id: number) => {
    try {
      await retryMessage(id);
      setMessages(prev =>
        prev.map(item => (item.id === id ? { ...item, status: 'PENDING', errorMessage: null } : item))
      );
      startPoll(id);
    } catch {
      // 请求层已经提示
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !(event.nativeEvent as any).isComposing) {
      event.preventDefault();
      void handleSend();
    }
  };

  const activeSession = sessions.find(item => item.id === activeId);
  const roundCount = messages.filter(item => item.role === 'user').length;
  const canSend = Boolean(text.trim() || imageUrls.length > 0) && !sending && !uploading;

  const renderMessage = (message: ChatMessage) => {
    if (message.role === 'user') {
      const inputImages = toImageList(message);
      return (
        <div className="msg-row user" key={message.id}>
          <div className="user-bubble">
            {inputImages.length > 0 && (
              <div className="message-input-images">
                {inputImages.map((url, index) => (
                  <Image
                    key={`${url}-${index}`}
                    src={url}
                    width={168}
                    className="message-input-image"
                    onLoad={() => scrollToBottom(false)}
                  />
                ))}
              </div>
            )}
            {message.content && <div className="message-text">{message.content}</div>}
          </div>
        </div>
      );
    }
    return (
      <div className="msg-row assistant" key={message.id}>
        <div className="assistant-avatar">AI</div>
        <div className="assistant-body">
          {(message.status === 'PENDING' || message.status === 'PROCESSING') && (
            <div className="typing">
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
              <em translate="no">
                {message.status === 'PENDING' ? '正在理解需求…' : '正在绘制，通常需要 30~60 秒…'}
              </em>
            </div>
          )}
          {message.status === 'SUCCESS' && (
            <>
              {message.content && <div className="message-text">{message.content}</div>}
              {message.outputImageUrl && (
                <div
                  className="result-image-wrap"
                  onClick={() => setRegionImageUrl(message.outputImageUrl || null)}
                  title="点击图片框选区域编辑"
                >
                  <Image
                    src={message.outputImageUrl}
                    className="message-result-image"
                    preview={false}
                    onLoad={() => scrollToBottom(false)}
                  />
                  <span className="result-image-hint">点击框选区域编辑</span>
                </div>
              )}
            </>
          )}
          {message.status === 'FAILED' && (
            <div className="failed-box">
              <div className="failed-text">{message.errorMessage || '生成失败'}</div>
              <Button size="mini" onClick={() => void handleRetry(message.id)}>
                重试
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="image-chat" translate="no">
      <aside className="chat-sidebar">
        <Button className="new-chat-button" icon={<IconPlus />} onClick={() => void handleNewSession()}>
          新对话
        </Button>
        <div className="session-list">
          {sessions.map(session => (
            <div
              key={session.id}
              className={`session-item${session.id === activeId ? ' active' : ''}`}
              onClick={() => void selectSession(session.id)}
            >
              <div className="session-main">
                <div className="session-title">{session.title || '新对话'}</div>
                <div className="session-time">{formatTime(session.lastMessageAt)}</div>
              </div>
              {session.running && <span className="session-running" title="任务进行中" />}
              <Popconfirm
                title="删除这个对话？"
                okText="删除"
                cancelText="取消"
                onOk={() => void handleDeleteSession(session.id)}
              >
                <Button
                  className="session-delete"
                  type="text"
                  size="mini"
                  icon={<IconDelete />}
                  onClick={event => event.stopPropagation()}
                />
              </Popconfirm>
            </div>
          ))}
          {sessions.length === 0 && <div className="session-empty">还没有对话</div>}
        </div>
      </aside>

      <section className="chat-main">
        <header className="chat-header">
          <div className="chat-title">{activeSession?.title || 'AI 生图对话'}</div>
          <div className="chat-subtitle">描述画面或上传参考图；多轮修改会自动带上上一张图</div>
          <div className="chat-memory">
            记忆保留最近 {MEMORY_ROUNDS} 轮
            {roundCount > 0 ? ` · 当前第 ${roundCount} 轮` : ''}
            {roundCount > MEMORY_ROUNDS && (
              <span className="memory-warning">已超出，更早的内容不再带入上下文，建议新建对话</span>
            )}
          </div>
        </header>

        <div className="chat-body" ref={bodyRef} onScroll={handleBodyScroll}>
          {messages.length === 0 ? (
            <div className="chat-empty">
              <div className="empty-avatar">AI</div>
              <div className="empty-title">想画点什么？</div>
              <div className="empty-desc">直接描述画面，或者先上传一张图让我修改。</div>
              <div className="suggestion-list">
                {SUGGESTIONS.map(item => (
                  <button key={item} className="suggestion" onClick={() => setText(item)}>
                    {item}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map(renderMessage)}
              {loadingMessages && <div className="loading-line">加载中…</div>}
            </>
          )}
        </div>
        <div className="composer-wrap">
          <div className="composer">
            {imageUrls.length > 0 && (
              <div className="composer-images">
                {imageUrls.map((url, index) => (
                  <div className="composer-image" key={`${url}-${index}`}>
                    <Image src={url} width={56} height={56} />
                    {index === imageUrls.length - 1 && <span className="base-badge">底图</span>}
                    <button
                      className="composer-image-remove"
                      onClick={() => setImageUrls(prev => prev.filter((_, i) => i !== index))}
                    >
                      <IconClose />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Input.TextArea
              className="composer-input"
              value={text}
              onChange={setText}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="描述你想要的画面，或粘贴/上传一张图让我修改…"
              autoSize={{ minRows: 1, maxRows: 6 }}
              disabled={sending}
            />
            <div className="composer-actions">
              <Upload
                beforeUpload={handleUpload}
                accept="image/png,image/jpeg,image/webp"
                showUploadList={false}
              >
                <Button type="text" icon={<IconImage />} loading={uploading} disabled={sending} />
              </Upload>
              <Button
                className="send-button"
                type="primary"
                shape="circle"
                icon={<IconSend />}
                loading={sending}
                disabled={!canSend}
                onClick={() => void handleSend()}
              />
            </div>
          </div>
          <div className="composer-tip">
            Enter 发送，Shift + Enter 换行 · 支持 Ctrl/⌘ + V 粘贴图片 · 每条消息生成一张图 · 记忆保留最近{' '}
            {MEMORY_ROUNDS} 轮
          </div>
        </div>
      </section>

      <RegionEditor
        visible={Boolean(regionImageUrl)}
        imageUrl={regionImageUrl}
        submitting={sending}
        onCancel={() => setRegionImageUrl(null)}
        onSubmit={(region, instruction) => void handleRegionSubmit(region, instruction)}
      />
    </div>
  );
}
